import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { differenceInDays, differenceInMonths, format, isBefore } from 'date-fns';
import { createContext, Key, ReactNode, useContext, useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Outlet, useNavigate, Link } from 'react-router-dom';
import BubbleModal from '../components/BubbleModal';
import Button from '../components/Button';
import FullscreenLoader from '../components/FullscreenLoader';
import { ReportItemType, ReportType } from '../helpers/types';
import { useApplicationContext } from '../providers/ApplicationProvider';
import { useAuthContext } from '../providers/AuthProvider';
import { useConfigContext } from '../providers/ConfigProvider';

type ContextType = {
    drafts: ReportItemType[];
    reports: ReportItemType[] | undefined;
    deleteDraft: (draftId: number, reportType: string) => void;
};

const Context = createContext<ContextType | null>(null);

const Provider = ({ children }: { children: ReactNode }) => {
    const { apiCall } = useAuthContext();
    const { reports } = useApplicationContext();
    const [drafts, setDrafts] = useState<ReportItemType[]>([]);

    useEffect(() => {
        if (reports) {
            setDrafts(reports.filter((r) => r.status === 9));
        }
    }, [reports]);

    return (
        <Context.Provider
            value={{
                drafts,
                reports,
                deleteDraft: async (draftId: number, reportType: string) => {
                    const response = await apiCall({
                        action: 'deleteReportDraftFromDB',
                        draftId,
                        reportType,
                    });

                    if (response?.data?.status === 200) {
                        setDrafts((prevDrafts) => {
                            return prevDrafts.filter((draft) => draft.id !== draftId);
                        });
                    }
                },
            }}
        >
            {reports ? children : <FullscreenLoader />}
        </Context.Provider>
    );
};

const ApplicationReports = () => (
    <Provider>
        <Outlet />
    </Provider>
);

const getItemCategory = (type: string, reportData: ReportType[] | null): 'report' | 'request' | null => {
    if(!reportData) return null;
    return reportData.find((r) => r.type === type)?.classification ?? null
}

// Er rapporttypen aktivert for denne søknadstypen (report_config / enabled_for)?
// Manglende nøkkel → behandles som aktivert (bakoverkompatibel fallback).
const isAvailableForAppType = (item: ReportType, appType: string): boolean => {
    const enabledFor = item.enabled_for ?? {};
    return !(appType in enabledFor) || enabledFor[appType] === true;
};

// Kan søker opprette en NY rapport av denne typen? Typer som ikke kan leveres
// flere ganger (multiple) kan kun opprettes når det ikke allerede finnes en
// rapport (utkast, forespurt eller innlevert) av samme type på søknaden.
const canCreateReportType = (item: ReportType, reports: ReportItemType[] | undefined): boolean => {
    if (item.multiple) return true;
    return !(reports ?? []).some((r) => r.type === item.type);
};

// Sorter rapporter/anmodninger etter rekkefølgen satt i report_config for
// søknadstypen (order_for). Typer uten rekkefølge havner sist (stabil sort).
const byConfigOrder = (appType: string) => (a: ReportType, b: ReportType): number => {
    const oa = a.order_for?.[appType] ?? Number.MAX_SAFE_INTEGER;
    const ob = b.order_for?.[appType] ?? Number.MAX_SAFE_INTEGER;
    return oa - ob;
};

// Status badge for submitted (inactive) reports
const getReportStatusDisplay = (status: number) => {
    switch (status) {
        case 2: return { label: 'Godkjent',        color: '#0e9740', icon: 'circle-check'  as const };
        case 3: return { label: 'Underkjent',       color: '#fc3b3b', icon: 'circle-xmark'  as const };
        case 4: return { label: 'Under behandling', color: '#d19e05', icon: 'circle-half-stroke' as const };
        case 5: return { label: 'Delvis godkjent',  color: '#9C27B0', icon: 'star-half-stroke' as const };
        default: return { label: 'Innlevert',       color: undefined,  icon: null };
    }
};

export const ApplicationReportsOverview = () => {
    const { t, i18nsubmittedRapporter, i18n  } = useTranslation();
    const { reportData, activeReportStatuses, disableIllustrations } = useConfigContext();
    const [modalActive, setModalActive] = useState<boolean>(false);
    const context = useContext(Context);
    const { application } = useApplicationContext();
    if (!context) {
        throw new Error('Context must be used within a Provider');
    }

    const { drafts, reports } = context;
    const appType = String(application?.type ?? '');

    const availableRapporter = (reportData ?? []).filter((item) =>
        isAvailableForAppType(item, appType) &&
        item.classification === 'report' &&
        canCreateReportType(item, reports)
    ).sort(byConfigOrder(appType));

    const rapportDrafts = drafts.filter((d) => {
        const cat = getItemCategory(d.type, reportData);
        return cat === 'report' || cat === null;
    });

    const activeRapporter = (reports ?? [])
        .filter((item) => {
            const cat = getItemCategory(item.type, reportData);
            return (cat === 'report' || cat === null) && activeReportStatuses.includes(item.status) && item.status !== 9;
        })
        .sort((a, b) => new Date(a.expireson).getTime() - new Date(b.expireson).getTime());

    const submittedRapporter = (reports ?? [])
        .filter((item) => {
            const cat = getItemCategory(item.type, reportData);
            return (cat === 'report' || cat === null) && !activeReportStatuses.includes(item.status);
        })
        .sort((a, b) => new Date(b.archivedon).getTime() - new Date(a.archivedon).getTime());

    const requestNames = (reportData ?? [])
    .filter((item) =>
        isAvailableForAppType(item, appType) &&
        item.classification === 'request' &&
        canCreateReportType(item, reports)
    )
    .sort(byConfigOrder(appType))
    .map((item) => item.short_description || item.title)
    .filter(Boolean);

    const requestList = new Intl.ListFormat(i18n.language === 'en' ? 'en' : 'nb', {
        type: 'disjunction',
    }).format(requestNames);
    return (
        <div className="row gx-5">
            <div className="col-lg-7">
                <h4 className="mb-3 mb-lg-5">{t('report_available_reports')}</h4>
                {rapportDrafts.map((item: ReportItemType, index: Key) => <ReportDraft key={index} {...item} />)}
                {activeRapporter.map((item, index) => <ReportItem key={index} {...item} />)}
                {availableRapporter.length > 0 && (
                    <Button title={t('report_create')} type="primary" rightIcon="arrow-right" onClick={() => setModalActive(!modalActive)} />
                )}

                {requestNames.length > 0 && application && (
                    <p className="mt-4">
                        <Trans
                            i18nKey="reports_requests_hint"
                            values={{ types: requestList }}
                            components={{
                                requestslink: (
                                    <Link
                                        to={`/soknad/${application.id}/forespørsler`}
                                        className="text-primary text-decoration-none"
                                    />
                                ),
                            }}
                        />
                    </p>
                )}
            </div>

            <div className="col-lg-5 mt-5 mt-lg-0">
                <h4 className="mb-3">{t('report_submitted')}</h4>
                {submittedRapporter.length ? (
                    submittedRapporter.map((item: ReportItemType, index: Key) => <ReportItem key={index} {...item} />)
                ) : (
                    <div><p><i>{t('reports_no_delivered')}</i></p></div>
                )}
            </div>

            <BubbleModal active={modalActive} setActive={setModalActive}>
                {!disableIllustrations && (
                    <img className="custom-modal-image mb-5" src={import.meta.env.VITE_API_BASE_URL + 'assets/svg/soknad-gfx.png'} alt={t('choose_application_type')} />
                )}
                <h2 className="mb-4">{t('report_what_to_send')}</h2>
                {availableRapporter.map((item, index) => (
                    <ReportChoice key={index} item={item} setModalActive={setModalActive} />
                ))}
                <a className="card-link text-decoration-none return-link" href="#" onClick={() => setModalActive(false)}>
                    <FontAwesomeIcon icon={['far', 'arrow-left']} className="me-1" />
                    {t('back')}
                </a>
            </BubbleModal>
        </div>
    );
};

export const ApplicationRequestsOverview = () => {
    const { t } = useTranslation();
    const { reportData, activeReportStatuses } = useConfigContext();
    const context = useContext(Context);
    const { application } = useApplicationContext();
    if (!context) throw new Error('Context must be used within a Provider');

    const { drafts, reports } = context;
    const appType = String(application?.type ?? '');

    const availableAnmodninger = (reportData ?? []).filter((item) =>
        isAvailableForAppType(item, appType) &&
        item.classification === 'request' &&
        canCreateReportType(item, reports)
    ).sort(byConfigOrder(appType));

    const anmodningDrafts = drafts.filter((d) => {
        const cat = getItemCategory(d.type, reportData);
        return cat === 'request';
    });

    const activeAnmodninger = (reports ?? [])
        .filter((item) => {
            const cat = getItemCategory(item.type, reportData);
            return cat === 'request' && activeReportStatuses.includes(item.status) && item.status !== 9;
        })
        .sort((a, b) => new Date(a.expireson).getTime() - new Date(b.expireson).getTime());

    const submittedAnmodninger = (reports ?? [])
        .filter((item) => {
            const cat = getItemCategory(item.type, reportData);
            return cat === 'request' && !activeReportStatuses.includes(item.status);
        })
        .sort((a, b) => new Date(b.archivedon).getTime() - new Date(a.archivedon).getTime());

    return (
        <div className="row gx-5">
            <div className="col-lg-7">
                <h4 className="mb-3 mb-lg-5">{t('requests_title')}</h4>
                {/* Drafts and active anmodninger appear at top, just like Rapporter */}
                {anmodningDrafts.map((item: ReportItemType, index: Key) => <ReportDraft key={index} {...item} />)}
                {activeAnmodninger.map((item, index) => <ReportItem key={index} {...item} />)}
                {/* Types shown directly as cards — no button */}
                {availableAnmodninger.map((item, index) => <RequestCard key={index} item={item} />)}
            </div>

            <div className="col-lg-5 mt-5 mt-lg-0">
                <h4 className="mb-3">{t('report_submitted')}</h4>
                {submittedAnmodninger.length ? (
                    submittedAnmodninger.map((item: ReportItemType, index: Key) => <ReportItem key={index} {...item} />)
                ) : (
                    <div><p><i>{t('requests_none_submitted')}</i></p></div>
                )}
            </div>
        </div>
    );
};


const ReportDraft = (props: ReportItemType) => {
    const { id, applicationId, templateName, expireson, type, change_original_report } = props;
    const { reportData, setReportDraftId, setSkipDraftChoice } = useConfigContext();
    const [url, setUrl] = useState<string | null>(null);
    const navigate = useNavigate();
    const { t } = useTranslation();

    const context = useContext(Context);
    if (!context) {
        throw new Error('Context must be used within a Provider');
    }

    const { deleteDraft } = context;

    const currentDate = new Date();
    const expiresDate = new Date(expireson);

    const expirationStatus = (() => {
        if (expireson) {
            if (isBefore(expiresDate, currentDate)) return 'past';
            const monthsDifference = differenceInMonths(expiresDate, currentDate);
            if (monthsDifference >= 1) return 'moreThanOneMonth';
            if (differenceInDays(expiresDate, currentDate) <= 30) return 'withinOneMonth';
        }

        return 'unknown';
    })();

    // Set class based on expiration status
    const cardClass = (() => {
        switch (expirationStatus) {
            case 'past':
                return 'card card-alert mb-4 border-danger text-decoration-none report cursor-pointer';
            case 'withinOneMonth':
                return 'card card-warning mb-4 border-warning text-decoration-none report cursor-pointer';
            case 'moreThanOneMonth':
                return 'card mb-4 border text-decoration-none report cursor-pointer';
            default:
                return 'card mb-4 border text-decoration-none cursor-pointer';
        }
    })();

    const handleClick = () => {
        setReportDraftId(id);
        setSkipDraftChoice(true);
        navigate(`/rapport/${applicationId}/${url}`);
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        deleteDraft(id, type);
    };

    useEffect(() => {
        if (reportData) {
            const reportType = reportData.filter((r) => r.type === type);
            if (reportType.length) {
                setUrl(reportType[0].url);
            }
        }
    }, [reportData]);

    if (url === null) return false;

    return (
        <div className={cardClass} onClick={handleClick}>
            <div className="card-body d-flex justify-content-between align-items-end position-relative">
                <div>
                    <h5 className="mb-1">
                        {templateName} ({change_original_report ? t('to_revise').toLowerCase() : t('started').toLowerCase()})
                    </h5>
                    <p className="card-text">
                        <span>#{applicationId}</span>
                    </p>
                </div>
                <div className="text-end d-flex flex-column">
                    {expireson && (
                        <span className={`fw-medium text-danger`}>
                            {t('deadline')} {format(expireson, 'dd.MM.yyyy')}
                        </span>
                    )}
                    <div className="d-flex gap-3">
                        <span className="card-link" onClick={handleDelete}>
                            {t('delete')}
                            <FontAwesomeIcon icon={['fal', 'xmark']} className="ms-1" />
                        </span>
                        <span className="card-link">
                            {t('fill_out')}
                            <FontAwesomeIcon icon={['fal', 'arrow-right']} className="ms-1" />
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const ReportItem = (props: ReportItemType) => {
    const { id, templateName, expireson, archivedon, status, applicationId, type, payload, started, applicationName, change_original_report } = props;
    // Fristlista på Dashboard får `started` fra REST og har ingen payload;
    // søknadsvisningen sender hele rapporten og har ingen `started`.
    const isStarted = started ?? Boolean(payload);
    const { activeReportStatuses, reportData, setReportDraftId, setSkipDraftChoice } = useConfigContext();
    const active = activeReportStatuses?.includes(status);
    const [url, setUrl] = useState<string | null>(null);
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Calculate expiration status
    const currentDate = new Date();
    const expiresDate = new Date(expireson);

    const expirationStatus = (() => {
        if (isBefore(expiresDate, currentDate)) return 'past';
        const monthsDifference = differenceInMonths(expiresDate, currentDate);
        if (monthsDifference >= 1) return 'moreThanOneMonth';
        if (differenceInDays(expiresDate, currentDate) <= 30) return 'withinOneMonth';
        return 'unknown';
    })();

    // Set class based on expiration status
    const cardClass = (() => {
        switch (expirationStatus) {
            case 'past':
                return 'card card-alert mb-4 border-danger text-decoration-none report';
            case 'withinOneMonth':
                return 'card card-warning mb-4 border-warning text-decoration-none report';
            case 'moreThanOneMonth':
                return 'card mb-4 border text-decoration-none report';
            default:
                return 'card mb-4 border text-decoration-none';
        }
    })();

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        setReportDraftId(id);
        setSkipDraftChoice(true);
        navigate(`/rapport/${applicationId}/${url}`);
    };

    const showDetails = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        const category = getItemCategory(type, reportData);
        const tab = category === 'request' ? 'forespørsler' : 'rapporter';
        navigate(`/soknad/${applicationId}/${tab}/${id}/detaljer`);
    };

    useEffect(() => {
        if (reportData) {
            const reportType = reportData.filter((r) => r.type === type);
            if (reportType.length) {
                setUrl(reportType[0].url);
            }
        }
    }, [reportData]);

    if (url === null) return false;

    if (active) { 
        return ( <a href="#" className={cardClass} onClick={handleClick}>
            <div className="card-body d-flex justify-content-between align-items-center">
                <div>
                    <h5 className="mb-1">
                        {/* Revisjonskladden dukker opp i fristkolonnen på
                            Dashboard, og der er det ReportItem som rendrer
                            den — ikke ReportDraft. Uten dette står kortet
                            bare med malnavnet, og søkeren får ingen pekepinn
                            om hvorfor skjemaet er tilbake. */}
                        {templateName} {change_original_report
                            ? `(${t('to_revise').toLowerCase()})`
                            : status === 0 && isStarted && `(${t('started').toLowerCase()})`}
                    </h5>
                    <p className="card-text text-start">
                        <span>#{applicationId}{applicationName ? `: ${applicationName}` : null}</span>
                    </p>
                </div>
                <div className="text-end d-flex flex-column">
                    <span className={`fw-medium ${expirationStatus === 'past' ? 'text-danger' : ''}`}>
                        {t('deadline')} {format(expiresDate, 'dd.MM.yyyy')}
                    </span>
                    <span className="card-link mt-1">
                        {t('fill_out')}
                        <FontAwesomeIcon icon={['fal', 'arrow-right']} className="ms-1" />
                    </span>
                </div>
            </div>
        </a>)
       
     } 
    
     const statusDisplay = getReportStatusDisplay(status);
     
    return (
        <a href="#" className="card card-submitted border text-decoration-none rounded-0 bg-body border-0 border-top report-submitted" onClick={showDetails}>
            <div className="card-body d-flex flex-column px-0">
                {/* Row 1: name left, status badge right */}
                <div className="d-flex justify-content-between align-items-start">
                    <h5 className="card-title text-primary mb-1">{templateName}</h5>
                    <span className="fw-medium ms-2 text-nowrap" style={{ color: statusDisplay.color }}>
                        {statusDisplay.label}
                        {statusDisplay.icon && (
                            <FontAwesomeIcon icon={['far', statusDisplay.icon]} className="ms-1" />
                        )}
                    </span>
                </div>
                {/* Row 2: submission date left, "Se →" on hover right */}
                <div className="d-flex justify-content-between">
                    <p className="card-text mb-0">
                        <span>{t('report_submitted')} {format(archivedon, 'dd.MM.yyyy')} kl. {format(archivedon, 'HH:mm')}</span>
                    </p>
                    <span className="card-link mt-1">
                        {t('see')}
                        <FontAwesomeIcon icon={['fal', 'arrow-right']} className="ms-1" />
                    </span>
                </div>
            </div>
        </a>
    );
};

const ReportChoice = ({ item, setModalActive }: { item: ReportType; setModalActive: (value: boolean) => void }) => {
    const { application } = useApplicationContext();
    const { setSkipDraftChoice } = useConfigContext();
    const navigate = useNavigate();

    const goToReport = () => {
        if (application) {
            setSkipDraftChoice(false);
            setModalActive(false);
            navigate(`/rapport/${application.id}/${item.url}`);
        }
    };
    return (
        <a href="#" className="card mb-3 border text-decoration-none w-100 next-gift-card" onClick={goToReport}>
            <div className="card-body d-flex justify-content-between align-items-center">
                <div>
                    <h5 className="card-title mb-1 fw-medium">{item.short_description}</h5>
                    <p className="card-text">
                        <span>{item.introtext}</span>
                    </p>
                </div>
                <div className="text-end d-flex flex-column">
                    <span className="mt-1">
                        <FontAwesomeIcon icon={['fal', 'arrow-right']} style={{ fontSize: 30 }} />
                    </span>
                </div>
            </div>
        </a>
    );
};

// NEW — card shown directly on Forespørsler tab; arrow hidden, appears on hover via .card-link CSS
const RequestCard = ({ item }: { item: ReportType }) => {
    const { application } = useApplicationContext();
    const { setSkipDraftChoice } = useConfigContext();
    const navigate = useNavigate();

    const goToReport = (e: React.MouseEvent) => {
        e.preventDefault();
        if (application) {
            setSkipDraftChoice(false);
            navigate(`/rapport/${application.id}/${item.url}`);
        }
    };

    return (
        <a href="#" className="card mb-3 text-decoration-none w-100" style={{ backgroundColor: '#dce9f8', border: '1px solid #b8d4f0', color: '#1a2e5a' }} onClick={goToReport}>
            <div className="card-body">
                <h5 className="card-title mb-1 fw-bold" style={{ color: '#1a488f' }}>{item.short_description}</h5>
                <div className="d-flex justify-content-between align-items-center">
                    <p className="card-text mb-0" style={{ color: '#1a488f' }}>{item.introtext}</p>
                    <span className="card-link fw-medium text-nowrap ms-3" style={{ color: '#1a488f' }}>
                        Fyll ut <FontAwesomeIcon icon={['fal', 'arrow-right']} className="ms-1" />
                    </span>
                </div>
            </div>
        </a>
    );
};

export default ApplicationReports;
